import { Link } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useQuery } from '@/api/query'
import { formatDate } from '@/lib/format'
import { Empty, ErrorState, Loading } from '@/ui/states'

type Health = Schema<'AccountHealthResponse'>
type Account = Schema<'AccountHealthOut'>
type Allowance = Schema<'AllowanceOut'>
type Factor = Schema<'RiskFactorOut'>

/**
 * Account health and ban risk — S-B31b, the panel `design/screens/outreach.html`
 * calls *the screen that prevents a ban*.
 *
 * **The inputs are the panel and the score is the footnote**, which is the
 * design's own instruction rather than a layout preference: *"ban risk is shown
 * as the inputs that produce it, not as an opaque score. A tenant who can see
 * 'you're at 18 of 20 posts today' understands the pause; one who sees only
 * 'risk: medium' does not."* So the two meters come first, the factors are
 * listed in full underneath, and the number sits beside them rather than
 * replacing them.
 *
 * **Nothing is computed here.** The caps, the percentages, the badge text and
 * the badge's colour all arrive from `/api/v1/outreach/accounts`. That is not
 * ceremony: the caps are `app/linkedin/actions.py`'s integers and the whole
 * claim of this screen is that it counts what the rate limiter counts, so a copy
 * of `20` in TypeScript would be a second definition of the cap waiting to
 * disagree with the first.
 *
 * **It is its own query, deliberately.** The funnel above and the deliverability
 * panel below each load separately, so a LinkedIn read that fails leaves the
 * other two on screen with their own data rather than blanking the dashboard.
 */
export function AccountHealthPanel(): JSX.Element {
  const health = useQuery(
    (signal) => call(() => api.GET('/api/v1/outreach/accounts', { signal })),
    [],
  )

  return (
    <section className="ar-stack">
      <h2 className="ar-h2">Account health</h2>

      {health.state.status === 'loading' ? (
        <Loading label="Loading your connected accounts" lines={3} />
      ) : null}

      {health.state.status === 'error' ? (
        <div className="ar-card">
          <ErrorState
            failure={health.state.failure}
            title="Could not load account health"
            onRetry={health.refetch}
          />
        </div>
      ) : null}

      {health.state.status === 'ready' ? <Ready data={health.state.data} /> : null}
    </section>
  )
}

function Ready({ data }: { data: Health }): JSX.Element {
  if (data.empty) {
    return (
      <div className="ar-card">
        <Empty
          icon="in"
          title="No LinkedIn account connected"
          body="Lead discovery runs on your own LinkedIn session, so there is nothing to watch until an account is connected. Once it is, this shows the day's allowances and anything that raises the risk of a restriction."
          action={
            <Link className="ar-btn ar-btn--primary" to="/settings/connections">
              Connect LinkedIn
            </Link>
          }
        />
      </div>
    )
  }
  return (
    <>
      <div className="ar-grid ar-grid--2">
        {data.accounts.map((account) => (
          <AccountCard key={account.account_key || account.label || 'default'} account={account} />
        ))}
      </div>
      {/*
        `design/screens/outreach.html:285`, and it was the one piece of that panel
        missing from this one (§I V7).

        It earns its place by answering the question the meters raise but do not
        address: a tenant reading "18 of 20 posts today" is being told how close
        the edge is, and nothing on the screen said what happens if it is crossed.
        The worry it settles — *does a LinkedIn problem stop my SEO too?* — is
        about the blast radius of a pause, not about the pause.

        Every clause is checked against the code rather than copied from the
        mockup, because a reassurance that is not true is worse than none:

          - *pause immediately* — `app/linkedin/limits.decide` refuses a paused
            account and `app/db/extension.bury_queued_searches` empties the queue
            behind it;
          - *email you* — `app/notifications/events.py:234` defines "LinkedIn
            paused or challenged" and marks it immediate, so
            `app/notifications/service.py` sends it at the moment it happens
            rather than in the next morning's digest;
          - *never retry* — `app/linkedin/pause.py` exists to guarantee there is
            no code path that un-pauses, only one a person presses;
          - *everything else keeps running* — the pause is a LinkedIn connection
            status, and `limits.decide` is the only thing that reads it.
      */}
      <div className="ar-alert ar-alert--warning" role="note">
        <span className="ar-alert__icon" aria-hidden="true">
          !
        </span>
        <div className="ar-alert__body">
          <strong>If LinkedIn shows a checkpoint</strong>
          We pause this account immediately, email you, and never retry. Everything else — email,
          WhatsApp, and all SEO work — keeps running.
        </div>
      </div>

      <p className="ar-subtle app-health__caption">
        Read {formatDate(data.checked_at)}. Both allowances are enforced in code, not by judgement,
        and they release at midnight IST.
      </p>
    </>
  )
}

function AccountCard({ account }: { account: Account }): JSX.Element {
  return (
    <article className="ar-card">
      <div className="ar-card__header">
        <h3>{account.label ?? 'LinkedIn account'}</h3>
        <span className="ar-card__spacer" />
        <span className={`ar-badge ar-badge--${account.badge.tone}`}>{account.badge.text}</span>
      </div>
      <div className="ar-card__body ar-stack">
        <p className="ar-muted app-health__badge-detail">{account.badge.detail}</p>

        {account.allowances.map((allowance) => (
          <Meter key={allowance.action} allowance={allowance} />
        ))}

        <hr className="app-health__rule" />

        <div className="ar-row app-health__score">
          <span className="ar-muted">Ban-risk score</span>
          <span className="ar-card__spacer" />
          {/* Tabular numerals and a fixed denominator: "48" alone is a number
              with no scale, and two cards side by side must be comparable. */}
          <span className="ar-mono">{account.risk_score} / 100</span>
        </div>

        <Reasons factors={account.factors} stopped={account.stopped} />
      </div>
    </article>
  )
}

/**
 * One allowance, as a meter and a fraction.
 *
 * `role="meter"` rather than `progressbar`: this is a measurement of a bounded
 * quantity that can go down as well as up, which is what a meter is for, and a
 * screen reader announcing "18 of 20 posts today" is the whole point of the
 * panel reaching somebody who cannot see the bar.
 */
function Meter({ allowance }: { allowance: Allowance }): JSX.Element {
  return (
    <div>
      <div className="ar-row app-health__meter-head">
        <span>{allowance.label}</span>
        <span className="ar-card__spacer" />
        <span className="ar-mono">
          {allowance.used} / {allowance.cap}
        </span>
      </div>
      <div
        className={`ar-meter ${allowance.near_limit ? 'ar-meter--over' : ''}`}
        role="meter"
        aria-label={`${allowance.label}: ${String(allowance.used)} of ${String(allowance.cap)}`}
        aria-valuenow={allowance.used}
        aria-valuemin={0}
        aria-valuemax={allowance.cap}
      >
        <div className="ar-meter__fill" style={{ width: `${String(allowance.percent)}%` }} />
      </div>
    </div>
  )
}

/**
 * Every factor behind the score, in words.
 *
 * **An empty list renders a sentence rather than nothing.** A card showing a
 * score of zero and no explanation reads as a panel that failed to load; *"No
 * risk factors on record"* reads as an account that is fine, and only one of
 * those is what a tenant should take away from a quiet week.
 */
function Reasons({ factors, stopped }: { factors: Factor[]; stopped: boolean }): JSX.Element {
  if (factors.length === 0) {
    return (
      <p className="ar-subtle app-health__reasons">
        No risk factors on record — nothing about this account's recent activity stands out.
      </p>
    )
  }
  return (
    <ul className="app-health__reasons">
      {factors.map((factor) => (
        <li key={factor.code}>
          <span className={stopped ? 'app-health__reason--stopped' : undefined}>
            {factor.detail}
          </span>{' '}
          <span className="ar-subtle">+{factor.points}</span>
        </li>
      ))}
    </ul>
  )
}
